import type { ModelPlanTargetNamespace, NamespaceRateFloorBreachRow } from '@nadoutong/contracts';
import { OpsApiError } from '../../api/client.ts';

/**
 * Display-side sales-floor helpers for the model-plan editor (#2292).
 *
 * Comparison matches the server: `rate + Number.EPSILON < floor`. This is
 * a display gate only — the 409 remains the authority.
 */

export type PlanFloorBreachSource = 'group_default' | 'sku_override';

export interface SkuResolvedRate {
  readonly sku_id: string;
  readonly sku_name: string;
  readonly namespace_id: string | null;
  readonly resolved_rate: number | null;
  readonly floor: number | null;
  readonly source: PlanFloorBreachSource | null;
}

export interface PlanFloorBreach {
  readonly sku_id: string;
  readonly sku_name: string;
  readonly namespace_id: string | null;
  readonly resolved_rate: number | null;
  readonly floor_multiplier: number | null;
  readonly source: PlanFloorBreachSource | null;
}

export interface SkuRateDraft {
  readonly sku_id: string;
  readonly rate: string;
}

export interface EntryRateDraft {
  readonly namespace_id: string;
  readonly default_rate: string;
}

export function fmtRate(value: number | null | undefined): string {
  return typeof value === 'number' && Number.isFinite(value) ? value.toFixed(4) : '—';
}

export function parsePositiveRate(raw: string): number | null {
  const trimmed = raw.trim();
  if (trimmed.length === 0) return null;
  const parsed = Number(trimmed);
  if (!Number.isFinite(parsed) || parsed <= 0) return null;
  return parsed;
}

export function isBelowSalesFloor(rate: number, floor: number): boolean {
  return rate + Number.EPSILON < floor;
}

function parseFloorBreachRows(raw: unknown): readonly NamespaceRateFloorBreachRow[] | null {
  if (!Array.isArray(raw) || raw.length === 0) return null;
  const breaches: NamespaceRateFloorBreachRow[] = [];
  for (const item of raw) {
    if (item === null || typeof item !== 'object' || Array.isArray(item)) return null;
    const row: Record<string, unknown> = { ...item };
    const skuId = row['sku_id'];
    const skuName = row['sku_name'];
    const floor = row['floor_multiplier'];
    if (typeof skuId !== 'string' || typeof skuName !== 'string') return null;
    if (floor !== null && typeof floor !== 'number') return null;
    breaches.push({ sku_id: skuId, sku_name: skuName, floor_multiplier: floor });
  }
  return breaches;
}

/**
 * Narrow `error.breaches` out of a `MODEL_PLAN_RATE_BELOW_SALES_FLOOR` 409.
 * Returns null for every other error, and for a malformed list, so the
 * caller falls through to ordinary error text rather than a partial table.
 */
export function modelPlanFloorBreachesFromError(
  err: unknown,
): readonly NamespaceRateFloorBreachRow[] | null {
  if (!isModelPlanFloorError(err)) {
    return null;
  }
  return parseFloorBreachRows(err.details?.['breaches']);
}

/**
 * Whether this is the floor-breach 409 at all, regardless of whether its
 * `breaches` payload parsed. Callers need the distinction because the
 * malformed-payload fallback must still be a HUMAN message — the generic
 * error path prints `code: message`, and the raw
 * MODEL_PLAN_RATE_BELOW_SALES_FLOOR string reaching an operator is exactly
 * what this module exists to prevent.
 */
export function isModelPlanFloorError(err: unknown): err is OpsApiError {
  return err instanceof OpsApiError && err.code === 'MODEL_PLAN_RATE_BELOW_SALES_FLOOR';
}

function indexTargets(namespaces: readonly ModelPlanTargetNamespace[]): {
  readonly nsBySkuId: ReadonlyMap<
    string,
    { readonly ns: ModelPlanTargetNamespace; readonly label: string; readonly floor: number | null }
  >;
} {
  const nsBySkuId = new Map<
    string,
    { readonly ns: ModelPlanTargetNamespace; readonly label: string; readonly floor: number | null }
  >();
  for (const ns of namespaces) {
    for (const sku of ns.skus) {
      nsBySkuId.set(sku.id, { ns, label: sku.label, floor: sku.sales_floor_multiplier });
    }
  }
  return { nsBySkuId };
}

export function resolvePlanSkuRates(
  entries: readonly EntryRateDraft[],
  skus: readonly SkuRateDraft[],
  namespaces: readonly ModelPlanTargetNamespace[],
): readonly SkuResolvedRate[] {
  const defaultByNs = new Map<string, number | null>();
  for (const entry of entries) {
    defaultByNs.set(entry.namespace_id, parsePositiveRate(entry.default_rate));
  }
  const { nsBySkuId } = indexTargets(namespaces);
  const result: SkuResolvedRate[] = [];
  for (const sku of skus) {
    const located = nsBySkuId.get(sku.sku_id);
    const groupDefault =
      located === undefined ? null : (defaultByNs.get(located.ns.id) ?? null);
    const overrideRaw = sku.rate.trim();
    if (overrideRaw.length > 0) {
      const override = parsePositiveRate(sku.rate);
      result.push({
        sku_id: sku.sku_id,
        sku_name: located?.label ?? sku.sku_id,
        namespace_id: located?.ns.id ?? null,
        resolved_rate: override,
        floor: located?.floor ?? null,
        source: override === null ? null : 'sku_override',
      });
      continue;
    }
    result.push({
      sku_id: sku.sku_id,
      sku_name: located?.label ?? sku.sku_id,
      namespace_id: located?.ns.id ?? null,
      resolved_rate: groupDefault,
      floor: located?.floor ?? null,
      source: groupDefault === null ? null : 'group_default',
    });
  }
  return result;
}

export function liveBreachesFromResolved(
  resolved: readonly SkuResolvedRate[],
): readonly PlanFloorBreach[] {
  const out: PlanFloorBreach[] = [];
  for (const row of resolved) {
    if (row.resolved_rate === null || row.floor === null) continue;
    if (!isBelowSalesFloor(row.resolved_rate, row.floor)) continue;
    out.push({
      sku_id: row.sku_id,
      sku_name: row.sku_name,
      namespace_id: row.namespace_id,
      resolved_rate: row.resolved_rate,
      floor_multiplier: row.floor,
      source: row.source,
    });
  }
  return out;
}

export function humanizeFloorBreaches(
  wire: readonly NamespaceRateFloorBreachRow[],
  resolved: readonly SkuResolvedRate[],
): readonly PlanFloorBreach[] {
  const byId = new Map<string, SkuResolvedRate>();
  for (const row of resolved) byId.set(row.sku_id, row);
  return wire.map((row) => {
    const local = byId.get(row.sku_id);
    return {
      sku_id: row.sku_id,
      sku_name: row.sku_name,
      namespace_id: local?.namespace_id ?? null,
      resolved_rate: local?.resolved_rate ?? null,
      floor_multiplier: row.floor_multiplier,
      source: local?.source ?? null,
    };
  });
}

export function groupDefaultCausesBreach(
  breaches: readonly PlanFloorBreach[],
  namespaceId: string,
): boolean {
  return breaches.some(
    (breach) => breach.source === 'group_default' && breach.namespace_id === namespaceId,
  );
}
